import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

interface RollingStatsChartProps {
  /** Rolling Sharpe ratios */
  rollingSharpe: number[];
  /** Rolling Sortino ratios */
  rollingSortino: number[];
  /** Rolling volatility */
  rollingVolatility: number[];
  /** Rolling beta */
  rollingBeta: number[];
  /** Start date (accounting for rolling window) */
  startDate: string;
  /** Rolling window in months */
  windowMonths?: number;
  /** Chart height */
  height?: number;
}

type MetricType = 'sharpe' | 'sortino' | 'volatility' | 'beta';

const metricConfig: Record<MetricType, { label: string; color: string; format: (v: number) => string; refLine?: number }> = {
  sharpe: {
    label: 'Sharpe Ratio',
    color: '#005AB5',
    format: (v) => v.toFixed(2),
    refLine: 1
  },
  sortino: {
    label: 'Sortino Ratio',
    color: '#059669',
    format: (v) => v.toFixed(2),
    refLine: 1.5
  },
  volatility: {
    label: 'Volatility (Ann.)',
    color: '#E66100',
    format: (v) => `${(v * 100).toFixed(1)}%`,
  },
  beta: {
    label: 'Beta',
    color: '#6B7280',
    format: (v) => v.toFixed(2),
    refLine: 1
  }
};

/**
 * Rolling statistics chart showing 6-month rolling metrics.
 * Helps visualize strategy consistency over time.
 */
export default function RollingStatsChart({
  rollingSharpe,
  rollingSortino,
  rollingVolatility,
  rollingBeta,
  startDate,
  windowMonths = 6,
  height = 250
}: RollingStatsChartProps) {
  const [selectedMetrics, setSelectedMetrics] = useState<MetricType[]>(['sharpe', 'sortino']);

  const data = useMemo(() => {
    // Offset start date by rolling window
    const start = new Date(startDate);
    start.setMonth(start.getMonth() + windowMonths);

    const maxLen = Math.max(
      rollingSharpe.length,
      rollingSortino.length,
      rollingVolatility.length,
      rollingBeta.length
    );

    return Array.from({ length: maxLen }, (_, i) => {
      const date = new Date(start);
      date.setDate(date.getDate() + i);

      return {
        date: date.toISOString().split('T')[0],
        sharpe: rollingSharpe[i] ?? null,
        sortino: rollingSortino[i] ?? null,
        volatility: rollingVolatility[i] ?? null,
        beta: rollingBeta[i] ?? null,
      };
    });
  }, [rollingSharpe, rollingSortino, rollingVolatility, rollingBeta, startDate, windowMonths]);

  const toggleMetric = (metric: MetricType) => {
    setSelectedMetrics(prev =>
      prev.includes(metric)
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload) return null;

    return (
      <div className="bg-gray-800 border border-gray-700 rounded p-3 shadow-lg">
        <p className="text-gray-400 text-xs mb-2">{label}</p>
        {payload.map((entry: any, i: number) => {
          const config = metricConfig[entry.dataKey as MetricType];
          return (
            <p key={i} className="text-sm" style={{ color: config.color }}>
              {config.label}:
              <span className="ml-2 font-medium tabular-nums">
                {config.format(entry.value)}
              </span>
            </p>
          );
        })}
      </div>
    );
  };

  // Calculate average for each metric
  const averages = {
    sharpe: rollingSharpe.length > 0 ? rollingSharpe.reduce((a, b) => a + b, 0) / rollingSharpe.length : 0,
    sortino: rollingSortino.length > 0 ? rollingSortino.reduce((a, b) => a + b, 0) / rollingSortino.length : 0,
    volatility: rollingVolatility.length > 0 ? rollingVolatility.reduce((a, b) => a + b, 0) / rollingVolatility.length : 0,
    beta: rollingBeta.length > 0 ? rollingBeta.reduce((a, b) => a + b, 0) / rollingBeta.length : 0,
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-white">Rolling Statistics ({windowMonths}-Month)</h3>
      </div>

      {/* Metric toggles */}
      <div className="flex flex-wrap gap-2 mb-4">
        {(Object.keys(metricConfig) as MetricType[]).map(metric => {
          const config = metricConfig[metric];
          const isSelected = selectedMetrics.includes(metric);

          return (
            <button
              key={metric}
              onClick={() => toggleMetric(metric)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                isSelected
                  ? 'border-transparent text-white'
                  : 'border-gray-700 text-gray-500 hover:text-gray-300'
              }`}
              style={{
                backgroundColor: isSelected ? config.color + '33' : 'transparent',
                borderColor: isSelected ? config.color : undefined
              }}
            >
              {config.label}
            </button>
          );
        })}
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="date"
            stroke="#6B7280"
            tick={{ fill: '#6B7280', fontSize: 10 }}
            tickLine={{ stroke: '#6B7280' }}
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getMonth() + 1}/${date.getFullYear().toString().slice(2)}`;
            }}
            interval="preserveStartEnd"
          />
          <YAxis
            stroke="#6B7280"
            tick={{ fill: '#6B7280', fontSize: 10 }}
            tickLine={{ stroke: '#6B7280' }}
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Reference lines for good/bad thresholds */}
          {selectedMetrics.includes('sharpe') && (
            <ReferenceLine y={1} stroke="#005AB5" strokeDasharray="3 3" strokeOpacity={0.5} />
          )}

          {/* Lines for selected metrics */}
          {selectedMetrics.map(metric => (
            <Line
              key={metric}
              type="monotone"
              dataKey={metric}
              stroke={metricConfig[metric].color}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {/* Averages summary */}
      <div className="mt-3 pt-3 border-t border-gray-800 grid grid-cols-4 gap-4 text-center">
        {selectedMetrics.map(metric => {
          const config = metricConfig[metric];
          return (
            <div key={metric}>
              <p className="text-xs text-gray-500">{config.label} Avg</p>
              <p className="text-sm font-medium tabular-nums" style={{ color: config.color }}>
                {config.format(averages[metric])}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
